// Promo de venta en una línea, igual en toda la venta: precio tachado → neto en verde y etiqueta verde.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';

/** Texto de la etiqueta cuando no se conoce ni la promo ni el código. */
export const SALE_PROMOTION_FALLBACK = 'Promoción';

function money(cents: number): string {
    return formatMXN(cents / 100);
}

/** Etiqueta verde: «Promo · CÓDIGO», solo la promo, «Código X» o «Promoción». */
export function SalePromotionTag({
    name,
    code,
    className,
}: {
    /** Nombre de la promo; puede ser la pieza de Promociones que lo resuelve. */
    name?: ReactNode;
    code?: string;
    className?: string;
}) {
    const hasName = name !== undefined && name !== null && name !== '';
    return (
        <span
            data-testid="sale-promotion-tag"
            className={cn(
                'inline-block max-w-full truncate rounded-full border border-success-soft-border bg-success-soft px-1.5 align-baseline text-[10.5px] font-medium leading-[1.5] text-success',
                className,
            )}
        >
            {hasName ? name : code ? `Código ${code}` : SALE_PROMOTION_FALLBACK}
            {hasName && code ? ` · ${code}` : ''}
        </span>
    );
}

/** Una etiqueta por promo de la línea; el código va con la primera (o solo, sin promos). */
export function SalePromotionTags({
    names,
    code,
    className,
}: {
    names: readonly ReactNode[];
    code?: string;
    className?: string;
}) {
    if (names.length <= 1) {
        return (
            <SalePromotionTag
                name={names[0]}
                {...(code !== undefined && { code })}
                {...(className !== undefined && { className })}
            />
        );
    }
    return (
        <span className={cn('inline-flex min-w-0 flex-wrap gap-1', className)}>
            {names.map((name, index) => (
                <SalePromotionTag
                    key={index}
                    name={name}
                    {...(index === 0 && code !== undefined && { code })}
                    className="min-w-0"
                />
            ))}
        </span>
    );
}

/** Precio de la línea con promo: el original tachado y el neto en verde; sin original, solo el neto. */
export function SalePromotionPrice({
    originalCents,
    netCents,
    className,
}: {
    originalCents: number | null;
    netCents: number;
    className?: string;
}) {
    return (
        <span data-testid="sale-promotion-price" className={cn('tabular-nums', className)}>
            {originalCents !== null && originalCents !== netCents && (
                <s className="mr-1.5 text-[0.88em] font-normal text-muted-foreground">
                    {money(originalCents)}
                </s>
            )}
            <span className="text-success">{money(netCents)}</span>
        </span>
    );
}

/** Renglón completo en una sola línea: etiqueta y luego el precio. */
export function SalePromotionLine({
    originalCents,
    netCents,
    name,
    names,
    code,
    className,
}: {
    originalCents: number | null;
    netCents: number;
    name?: ReactNode;
    /** Varias promos en la línea: una etiqueta por promo. */
    names?: readonly ReactNode[];
    code?: string;
    className?: string;
}) {
    return (
        <div
            data-testid="sale-promotion-line"
            className={cn('flex min-w-0 items-baseline justify-end gap-2', className)}
        >
            <SalePromotionTags
                names={names ?? (name === undefined ? [] : [name])}
                {...(code !== undefined && { code })}
                className="min-w-0"
            />
            <SalePromotionPrice
                originalCents={originalCents}
                netCents={netCents}
                className="shrink-0"
            />
        </div>
    );
}
